// TODO: Review AI overlay dark mode contrast ratios with UX - https://taskei.amazon.dev/tasks/NHE-5673
import "./AIAutomationOverlay.scss";

import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import {
  IconAlertCircleSmall,
  IconMinusCircleFillSmall,
} from "@amzn/stencil-react-icons";
import { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { WINDOWS_VPN_UI_TEXT } from "@/components/ConnectVpn/constants";
import { useAgentProgress } from "@/hooks/useAgentProgress";
import { useAivaBannerCollapse } from "@/stores/contexts/AivaBannerContext";

import aiUserInputIcon from "../../assets/icons/aiUserInput.svg";
import aivaIcon from "../../assets/images/aivaIcon.svg";
import { getOS, OPERATING_SYSTEM } from "../../helpers/os-helper";
import { AppContext } from "../../stores/appStore";
import TypingIndicator from "../Commons/TypingIndicator";
import { MANUAL_FALLBACK_THRESHOLD } from "../constants";

// Constants for card width
const CARD_WIDTH = 700;
const CARD_WIDTH_USER_TURN = 950;
/*
 * The resting banner. Wider than the design's 374, because Stencil's smallest usable type still runs
 * larger than the mock's and the title wrapped to three lines at that width.
 */
const CARD_WIDTH_RESTING = 440;

// Names the banner so both the minimize and the expand control can point `aria-controls` at it.
const CARD_ELEMENT_ID = "ai-automation-card";

// Text colour for the card's two light fills. Also `$color-text-on-light` in the stylesheet, which
// colours the controls on them -- change both together.
const DARK_TEXT = "#232F3E";

/**
 * How much of the banner is showing.
 *
 * `resting` is the compact banner Aiva shows while a recording is open but it is not touching the
 * screen; `full` is the banner it grows into while driving, and the one it stays at until a failure
 * is acknowledged. Minimising to the icon is tracked separately, since that is the hire's choice.
 */
type AivaBannerSize = "resting" | "full";

interface AIAutomationOverlayProps {
  /** Ends the run, or acknowledges a failed one. Absent at rest, where no run is in flight. */
  onStopAutomation?: () => void;
  isFailed?: boolean;
  failureCount?: number;
  onManualFallback?: () => void;
  size?: AivaBannerSize;
  /** Whether the hire switched capture off. Their consent stands, and resuming re-asks nothing. */
  isPaused?: boolean;
  /** Pauses background capture. Optional for a caller with no capture to offer. */
  onPauseCapture?: () => void;
  /** Resumes it under the consent already given, for the same callers. */
  onResumeCapture?: () => void;
}

/**
 * The numbered sign-in steps on a user-turn card.
 *
 * Both flows render the same ordered list in the same one allowed colour, so each supplies only its
 * own steps. The colour is hardcoded, because the user-turn card has a static light gradient whatever
 * the theme mode is.
 */
const UserTurnSteps = ({
  children,
  dataTestId,
}: {
  children: ReactNode;
  dataTestId: string;
}): JSX.Element => (
  <Text
    style={{ color: DARK_TEXT }}
    fontSize="T200"
    className="ai-text-wrap"
    dataTestId={dataTestId}
  >
    <ol className="ai-prompt-steps">{children}</ol>
  </Text>
);

/**
 * Sub-component that displays VPN username and PIN instructions for PENDING_USER_ACTION state.
 * Shows numbered steps and warning with alert icon.
 *
 * These describe the legacy flow, where the credential fields belong to the Cisco client's own
 * pop-up. They stay the default for every response that does not name a flow, which leaves an agent
 * that says nothing exactly where it was before `auth_location` existed.
 */
const VpnUsernamePinInstructions = ({
  username,
}: {
  username?: string;
}): JSX.Element => (
  <Col gridGap="S200">
    <UserTurnSteps dataTestId="clientSignInSteps">
      <li>
        <FormattedMessage
          id={
            username
              ? "connectVpn-ai-automation-prompt-step1-with-username"
              : "connectVpn-ai-automation-prompt-step1"
          }
          values={username ? { username } : undefined}
        />
      </li>
      <li>
        <FormattedMessage id="connectVpn-ai-automation-prompt-step2" />
      </li>
    </UserTurnSteps>
    <Row gridGap="S200" alignItems="flex-start">
      <IconAlertCircleSmall
        style={{ color: DARK_TEXT }}
        margin={{ top: "dimensions.spacing.100" }}
        aria-hidden={true}
      />
      <Text
        // TODO: Review AI disclosure dark mode styling with UX - https://taskei.amazon.dev/tasks/NHE-5673
        style={{ color: DARK_TEXT }}
        fontSize="T200"
        className="ai-text-wrap"
        dataTestId="promptWarningText"
      >
        <FormattedMessage id="connectVpn-ai-automation-prompt-warning" />
      </Text>
    </Row>
  </Col>
);

/**
 * The manual SAML step list's own keys for the browser hand-off, in order.
 *
 * Reused rather than duplicated into overlay-specific keys: they already say exactly this, they are
 * already translated into every locale we ship, and the hire read the same three sentences on the
 * manual card before the agent took over. The `-mac-` in the names is historical — the browser flow
 * is the same on Windows, and only the passkey dialog differs: its title, its "Security Key PIN"
 * field and its "OK" button. WINDOWS_VPN_UI_TEXT supplies those two labels, so the card names the
 * buttons the hire can actually see.
 */
const BROWSER_SIGN_IN_STEP_IDS = [
  "connectOrcaCorpVpn-expander-body-mac-step4",
  "connectOrcaCorpVpn-expander-body-mac-step5",
  "connectOrcaCorpVpn-expander-body-mac-step6",
] as const;

/**
 * Sub-component that displays Midway sign-in instructions for PENDING_USER_ACTION state under the
 * SAML flow, where Cisco hands authentication to the default browser.
 *
 * No warning row. The legacy card warns against clicking the Cisco pop-up's `OK`, and this flow has
 * no such button: the security-key touch is what submits.
 */
const VpnBrowserSignInInstructions = (): JSX.Element => (
  <UserTurnSteps dataTestId="browserSignInSteps">
    {BROWSER_SIGN_IN_STEP_IDS.map((id) => (
      <li key={id}>
        {/* Only the Windows agent reports a browser sign-in, so these are Windows labels. */}
        <FormattedMessage id={id} values={WINDOWS_VPN_UI_TEXT} />
      </li>
    ))}
  </UserTurnSteps>
);

/**
 * The full-screen border that tells the hire their screen is being driven. Absent at rest, where it
 * would claim screen control of a recording that only watches.
 *
 * The chip and typing indicator are not here: they belong to the step, and {@link AivaStepStatus}
 * renders them in its body.
 */
const AIAutomationActiveState = ({
  animate,
  isUserTurn,
  isResting,
}: {
  animate: boolean;
  isUserTurn: boolean;
  isResting: boolean;
}): JSX.Element | null =>
  isResting ? null : (
    <View
      className={`ai-automation-overlay ${animate ? "animate" : ""} ${
        isUserTurn ? "user-turn" : ""
      }`}
      dataTestId="aiAutomationBorder"
    />
  );

/**
 * Component that displays during AI automation workflows.
 * Shows real-time agent progress updates and provides a stop button.
 * When isFailed is true, shows error state with "Back to set up" button.
 * When isUserTurn is true (PENDING_USER_ACTION), shows VPN username/PIN instructions.
 */
export default function AIAutomationOverlay({
  onStopAutomation,
  isFailed = false,
  failureCount = 0,
  onManualFallback,
  size = "full",
  isPaused = false,
  onPauseCapture,
  onResumeCapture,
}: AIAutomationOverlayProps): JSX.Element {
  const isResting = size === "resting";
  /* Only the resting banner has a paused form; a run in flight is by definition not paused. */
  const isDisabled = isResting && isPaused;
  const { state } = useContext(AppContext);
  const { isCollapsed, setCollapsed } = useAivaBannerCollapse();
  /*
   * Only the resting banner collapses. While Aiva is driving the screen the banner is the notice that
   * it is, so it offers no way to dismiss it -- and a choice made at rest is ignored, not lost, for as
   * long as the run lasts.
   */
  const isMinimized = isCollapsed && isResting;
  const intl = useIntl();
  const minimizeButtonRef = useRef<HTMLButtonElement>(null);
  const expandButtonRef = useRef<HTMLButtonElement>(null);
  /*
   * Which control to focus once the swap has rendered, or null to leave focus alone. Focus moves only
   * because the hire pressed something: this package deliberately takes no focus on mount, since doing
   * so drew a focus ring on a hire who had clicked nothing.
   */
  const controlToFocusRef = useRef<"minimize" | "expand" | null>(null);
  const [animate, setAnimate] = useState(false);

  /*
   * Read through the shared hook rather than held here, because this banner is long-lived: it shrinks
   * back to resting instead of unmounting, and state kept locally survived that. A finished run then
   * left its own progress on the resting banner -- the hire's PIN steps under "enabled in the
   * background", and the next run opening on the last one's status line.
   */
  const isRunInFlight = !isFailed && !isResting;
  const {
    message,
    isUserTurn: wasHandedBack,
    isProcessing: wasTurnInFlight,
    authLocation,
  } = useAgentProgress(isRunInFlight);
  /*
   * Clamped on the run rather than left to the hook's reset, which is a passive effect: a hire who
   * stops mid-turn flips `size` to resting in the same commit, and React paints that frame before the
   * effect clears anything -- so the resting banner wore the light user-turn fill with the PIN steps
   * under "enabled in the background". The same window reached the failed banner's typing indicator.
   *
   * Not pinned by a test yet, and worth one: deleting either term leaves the whole suite green, because
   * istanbul never reports the unevaluated side of an `&&`. An ordinary assertion cannot see it, since
   * Testing Library flushes effects inside `rerender` -- but a parent holding `size` in state and reading
   * the card's className from its own `useLayoutEffect` does, because that runs in the commit that
   * applied the size and before this hook's passive reset.
   */
  const isUserTurn = wasHandedBack && isRunInFlight;
  const isProcessing = wasTurnInFlight && isRunInFlight;
  const agentStatus = isFailed
    ? intl.formatMessage({ id: "connectVpn-ai-automation-failed-message" })
    : message ??
      intl.formatMessage({ id: "connectVpn-ai-automation-cursor-warning" });

  useEffect(() => {
    setAnimate(true);
  }, []);

  useEffect(() => {
    const control = controlToFocusRef.current;
    if (!control) {
      return;
    }
    controlToFocusRef.current = null;
    const target =
      control === "expand"
        ? expandButtonRef.current
        : minimizeButtonRef.current;
    target?.focus();
  }, [isMinimized]);

  const handleCollapseClick = () => {
    controlToFocusRef.current = "expand";
    setCollapsed(true);
  };

  const handleExpandClick = () => {
    controlToFocusRef.current = "minimize";
    setCollapsed(false);
  };

  const handleStopClick = () => {
    // After multiple failures (at threshold or more), go directly to manual instructions
    if (
      isFailed &&
      failureCount >= MANUAL_FALLBACK_THRESHOLD &&
      onManualFallback
    ) {
      onManualFallback();
    } else {
      onStopAutomation?.();
    }
  };

  /*
   * Switching capture off changes the title, the fill and the button, and none of that reaches a screen
   * reader on its own -- on the one control whose whole purpose is stopping the recording. Announced
   * from the same `role="status"` pattern the permissions walkthrough uses, and only on a change, so
   * arriving at the banner does not speak.
   */
  const [captureAnnouncement, setCaptureAnnouncement] = useState("");
  /* Normalised, because the prop is optional and `undefined` is this ref's not-yet-read sentinel. */
  const isCapturePaused = isPaused === true;
  const announcedPause = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (!isResting || announcedPause.current === isCapturePaused) {
      return;
    }
    const isFirstReading = announcedPause.current === undefined;
    announcedPause.current = isCapturePaused;
    if (isFirstReading) {
      return;
    }
    setCaptureAnnouncement(
      intl.formatMessage({
        id: isCapturePaused
          ? "connectVpn-ai-automation-disabled-title"
          : "connectVpn-ai-automation-background-title",
      })
    );
  }, [intl, isCapturePaused, isResting]);

  /* The card's two light fills, which need dark text rather than the white the gradients use. */
  const isOnLightFill = isUserTurn || isDisabled;

  // Helper function to determine the title message ID based on state
  const getTitleMessageId = (): string => {
    if (isDisabled) return "connectVpn-ai-automation-disabled-title";
    if (isResting) return "connectVpn-ai-automation-background-title";
    if (isFailed) {
      return failureCount >= MANUAL_FALLBACK_THRESHOLD
        ? "connectVpn-ai-automation-unableToComplete-title"
        : "connectVpn-ai-automation-failed-notice";
    }
    if (isUserTurn) return "connectVpn-ai-automation-user-turn-notice";
    return "connectVpn-ai-automation-screen-control-notice";
  };

  /**
   * The accessible name for the collapsed mark, which is the only thing it conveys.
   *
   * Two cases, because only the resting banner collapses: whether capture is running or the hire
   * switched it off. A minimised hire has nothing else left to read that from.
   */
  const getCollapsedLabelId = (): string =>
    isDisabled
      ? "connectVpn-ai-automation-expand-label-disabled"
      : "connectVpn-ai-automation-expand-label-background";

  // Helper function to determine the button text message ID
  const getButtonTextId = (): string => {
    if (isFailed) {
      return failureCount >= MANUAL_FALLBACK_THRESHOLD
        ? "connectVpn-ai-disclosure-decline-button"
        : "connectVpn-ai-automation-back-to-setup-button";
    }
    return "connectVpn-ai-automation-stop-button";
  };

  // The resting banner's one control. Both are session-scoped and touch no stored consent.
  const restingAction = isDisabled
    ? {
        labelId: "connectVpn-ai-automation-enable-button",
        onPress: onResumeCapture,
        testId: "aiEnableButton",
      }
    : {
        labelId: "connectVpn-ai-automation-stop-button",
        onPress: onPauseCapture,
        testId: "aiPauseButton",
      };

  const osType = getOS();
  /*
   * Only the full banner keeps the per-OS dock. [NHE-5376] moved it to the top on Windows because the
   * Cisco VPN client GUI covered it at the bottom, and no right-hand anchor has been checked against
   * that. The resting banner and the collapsed mark dock top-right, which is where the design puts
   * them and where nothing is driving the screen for a native window to fight over.
   */
  const perOsPositionClassName =
    osType === OPERATING_SYSTEM.WINDOWS ? "windows-position" : "macos-position";
  const positionClassName =
    isResting || isMinimized ? "top-right-position" : perOsPositionClassName;
  const cardClassName = [
    "ai-automation-card",
    animate ? "animate" : "",
    positionClassName,
    isMinimized ? "collapsed" : "",
    isResting ? "resting" : "",
    isDisabled ? "disabled" : "",
    isUserTurn ? "user-turn-active" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Col gridGap="S400" dataTestId="aiAutomationOverlay">
      <AIAutomationActiveState
        animate={animate}
        isUserTurn={isUserTurn}
        isResting={isResting}
      />

      {/* One card for every state, so the banner animates between them instead of being swapped out.
          Keeping it mounted is also what makes `aria-controls` resolve while it is minimised. */}
      <Card
        id={CARD_ELEMENT_ID}
        className={cardClassName}
        padding={{ top: "S400", bottom: "S400", left: "S200", right: "S200" }}
        // The minimised square is sized in one place, the stylesheet, so no width is passed for it.
        width={
          isMinimized
            ? undefined
            : isResting
            ? CARD_WIDTH_RESTING
            : isUserTurn
            ? CARD_WIDTH_USER_TURN
            : CARD_WIDTH
        }
        dataTestId="aiAutomationCard"
      >
        {isMinimized ? (
          <Button
            ref={expandButtonRef}
            variant={ButtonVariant.Tertiary}
            className="ai-automation-expand-button"
            onClick={handleExpandClick}
            aria-label={intl.formatMessage({ id: getCollapsedLabelId() })}
            aria-expanded={false}
            aria-controls={CARD_ELEMENT_ID}
            dataTestId="aiExpandButton"
            icon={<img src={aivaIcon} alt="" className="ai-icon-collapsed" />}
          />
        ) : (
          <>
            {/* Only at rest. The automation banner is the notice that Aiva is driving the screen, so
                it offers nothing to dismiss it with. */}
            {isResting && (
              <Button
                ref={minimizeButtonRef}
                variant={ButtonVariant.Tertiary}
                className="ai-automation-minimize-button"
                onClick={handleCollapseClick}
                aria-label={intl.formatMessage({
                  id: "connectVpn-ai-automation-minimize-label",
                })}
                aria-expanded={true}
                aria-controls={CARD_ELEMENT_ID}
                dataTestId="aiMinimizeButton"
                icon={<IconMinusCircleFillSmall aria-hidden={true} />}
              />
            )}
            <Row
              className="ai-button-container"
              justifyContent="space-between"
              alignItems="center"
              gridGap="S200"
            >
              <Row gridGap="S200" alignItems="flex-start" flex={1}>
                <img
                  src={isUserTurn ? aiUserInputIcon : aivaIcon}
                  alt={isUserTurn ? "VPN" : "Aiva"}
                  className={`ai-icon-large${
                    isUserTurn ? " ai-icon-centered" : ""
                  }${isResting ? " ai-icon-resting" : ""}`}
                />
                {/* The resting banner drops the inset: at 374px wide the padding costs the title a
                    whole extra line, and the design does not indent it. */}
                <Col
                  gridGap="S100"
                  padding={
                    isResting
                      ? undefined
                      : {
                          top: "dimensions.spacing.200",
                          left: "dimensions.spacing.250",
                        }
                  }
                  flex={1}
                >
                  <Row alignItems="flex-end" gridGap="S100">
                    <Text
                      fontWeight="bold"
                      fontSize={isResting ? "T200" : "T300"}
                      // Hardcoded: the dark gradient sizes take white, the two light ones dark navy.
                      style={{ color: isOnLightFill ? DARK_TEXT : "white" }}
                    >
                      <FormattedMessage id={getTitleMessageId()} />
                    </Text>
                    {isProcessing && !isUserTurn && (
                      <TypingIndicator size="sm" />
                    )}
                  </Row>
                  {/* One line under the title in every state: the paused banner says how to come
                      back, the resting one points at the step, and a run reports its progress. */}
                  {isUserTurn ? (
                    // Only "browser" branches. Every other value, including none at all, keeps the
                    // legacy Cisco-pop-up steps: that flow is still live, and its copy is the one
                    // this card has always shown.
                    authLocation === "browser" ? (
                      <VpnBrowserSignInInstructions />
                    ) : (
                      <VpnUsernamePinInstructions
                        username={state.amazonUsername}
                      />
                    )
                  ) : (
                    <Text
                      color={undefined}
                      style={{ color: isOnLightFill ? DARK_TEXT : "white" }}
                      // The resting banner is a quarter the full one's width, and takes the design's
                      // smaller scale so its title fits the two lines the 86px card is sized for.
                      fontSize={isResting ? "T100" : "T200"}
                      className="ai-text-wrap"
                      dataTestId="agentStatusMessage"
                    >
                      {isDisabled ? (
                        <FormattedMessage id="connectVpn-ai-automation-disabled-description" />
                      ) : isResting ? (
                        <FormattedMessage id="connectVpn-ai-automation-background-description" />
                      ) : isFailed &&
                        failureCount >= MANUAL_FALLBACK_THRESHOLD ? (
                        <FormattedMessage id="connectVpn-ai-automation-unableToComplete-description" />
                      ) : (
                        agentStatus
                      )}
                    </Text>
                  )}
                </Col>
              </Row>
              {/* Keyed apart: these two mean different things, and one shared slot let React carry
                  the same button element from one to the other. */}
              {isResting
                ? restingAction.onPress !== undefined && (
                    <Button
                      key={restingAction.testId}
                      onClick={restingAction.onPress}
                      dataTestId={restingAction.testId}
                    >
                      <FormattedMessage id={restingAction.labelId} />
                    </Button>
                  )
                : onStopAutomation !== undefined && (
                    <Button
                      key="aiStopButton"
                      onClick={handleStopClick}
                      dataTestId="aiStopButton"
                    >
                      <FormattedMessage id={getButtonTextId()} />
                    </Button>
                  )}
            </Row>
          </>
        )}
      </Card>
      {/* Outside the card, so collapsing it cannot take the announcement's element with it. */}
      <View
        role="status"
        aria-live="polite"
        className="ai-capture-announcement"
      >
        {captureAnnouncement}
      </View>
    </Col>
  );
}
